import React, { useEffect, useState, useMemo } from "react";
import { Box, Grid, Typography, useTheme, Skeleton } from "@mui/material";
import CommonCard from "../../common/components/CommonCard";
import moment from "moment";
import { East } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { setLoading } from "../../features/authSlice";
import { getBlogList } from "../../api/blogApi";
import { showNotification } from "../../common/components/Notification/ToastifyNotification";
import SimplePagination from "./SimplePagination";
import { BlogStatus } from "../../common/const";
import LoadingSpinner from "../../common/components/LoadingSpinner";
import { useNavigate } from "react-router-dom";

// Cache for images
const imageCache = new Map();

const DummyImage = () => (
    <Box 
        sx={{ 
            width: '100%', 
            height: '200px', 
            bgcolor: '#f0f0f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            gap: 1
        }}
    >
        <Box 
            sx={{ 
                width: '50px', 
                height: '50px', 
                borderRadius: '50%',
                bgcolor: '#e0e0e0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}
        >
            <Typography fontSize={24} color="#999">📷</Typography>
        </Box>
        <Typography color="#999" fontSize={14}>Image not available</Typography>
    </Box>
);

const Blogs = ({ blogsRef, fromBlog }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { loading } = useSelector((state) => state.auth);
    const [blogs, setBlogs] = useState(null);
    const [pageNo, setPageNo] = useState(1);
    const [imageUrls, setImageUrls] = useState({});
    const [loadingImages, setLoadingImages] = useState({});
    const pageSize = 3;

    // Preload images in parallel with concurrency limit
    const loadImagesInParallel = async (blogsData, concurrencyLimit = 3) => {
        const blogsWithImages = blogsData.filter(blog => blog.thumbnail_url);
        
        // Initialize loading states for all blogs
        const initialLoadingStates = {};
        blogsWithImages.forEach(blog => {
            initialLoadingStates[blog.blog_id] = true;
        });
        setLoadingImages(initialLoadingStates);

        // Check cache first
        const uncachedBlogs = blogsWithImages.filter(blog => !imageCache.has(blog.thumbnail_url));
        const cachedResults = {};

        blogsWithImages.forEach(blog => {
            if (imageCache.has(blog.thumbnail_url)) {
                cachedResults[blog.blog_id] = imageCache.get(blog.thumbnail_url);
            }
        });

        // Immediately set cached images
        if (Object.keys(cachedResults).length > 0) {
            setImageUrls(prev => ({...prev, ...cachedResults}));
            const cachedLoadingStates = {};
            Object.keys(cachedResults).forEach(blogId => {
                cachedLoadingStates[blogId] = false;
            });
            setLoadingImages(prev => ({...prev, ...cachedLoadingStates}));
        }

        // Load uncached images in parallel with concurrency limit
        const results = { ...cachedResults };
        
        for (let i = 0; i < uncachedBlogs.length; i += concurrencyLimit) {
            const batch = uncachedBlogs.slice(i, i + concurrencyLimit);
            const batchPromises = batch.map(async (blog) => {
                try {
                    const url = await getImage(blog);
                    // Cache the image
                    imageCache.set(blog.thumbnail_url, url);
                    return { blogId: blog.blog_id, url };
                } catch (error) {
                    console.error('Failed to load image for blog:', blog.blog_id, error);
                    return { blogId: blog.blog_id, url: null };
                }
            });

            const batchResults = await Promise.all(batchPromises);
            
            // Update state for each batch
            batchResults.forEach(({ blogId, url }) => {
                results[blogId] = url;
                setImageUrls(prev => ({...prev, [blogId]: url}));
                setLoadingImages(prev => ({...prev, [blogId]: false}));
            });
        }
    };

    const getBlogs = async () => {
        try {
            dispatch(setLoading({ loading: true }));
            const response = await getBlogList(pageNo, pageSize);
            setBlogs(response.data);
            
            if (response.data?.data) {
                // Load images in parallel
                await loadImagesInParallel(response.data.data);
            }
            
            dispatch(setLoading({ loading: false }));
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || 
                               err.response?.data?.message || 
                               err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({ loading: false }));
        }
    };

    useEffect(() => {
        getBlogs();
    }, [pageNo]);

    const handlePageChange = (page) => {
        setPageNo(page);
        // Don't clear image URLs entirely, keep cache
        setImageUrls({});
        setLoadingImages({});
    };

    const getImage = async (blog) => {
        try {
            // Check cache first
            if (imageCache.has(blog.thumbnail_url)) {
                return imageCache.get(blog.thumbnail_url);
            }

            // Add timeout to fetch
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

            const responseData = await fetch(
                `${process.env.REACT_APP_BASE_URL}/file?bname=${blog.thumbnail_url}&download=false`,
                {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    },
                    signal: controller.signal,
                    cache: 'force-cache' // Use browser cache
                }
            );
            
            clearTimeout(timeoutId);

            if (!responseData.ok) {
                throw new Error('Failed to fetch image');
            }
            
            const blob = await responseData.blob();
            return URL.createObjectURL(blob);
        } catch (error) {
            if (error.name === 'AbortError') {
                console.error('Image fetch timeout for blog:', blog.blog_id);
            }
            throw error;
        }
    };

    // Optimize image rendering with lazy loading
    const renderImage = (blog) => {
        const imageUrl = imageUrls[blog.blog_id];
        const isLoading = loadingImages[blog.blog_id];
        
        if (isLoading) {
            return (
                <Skeleton 
                    variant="rectangular" 
                    width="100%" 
                    height={200} 
                    animation="wave"
                    sx={{ bgcolor: 'grey.200' }}
                />
            );
        }
        
        if (imageUrl) {
            return (
                <img 
                    src={imageUrl} 
                    alt={blog.short_description || "blog"} 
                    loading="lazy" // Add native lazy loading
                    style={{ 
                        width: '100%', 
                        height: '250px', 
                        display: 'block',
                    }}
                />
            );
        }
        
        return <DummyImage />;
    };

    // Clean up object URLs on unmount
    useEffect(() => {
        return () => {
            // Only revoke URLs that are not in cache
            Object.values(imageUrls).forEach(url => {
                if (url && url.startsWith('blob:') && !Array.from(imageCache.values()).includes(url)) {
                    URL.revokeObjectURL(url);
                }
            });
        };
    }, [imageUrls]);

    const publishedBlogs = useMemo(() => 
        blogs?.data?.filter((i) => i.status === BlogStatus.Published) || [], 
        [blogs]
    );

    return (
        <Box
            id="blogs"
            ref={blogsRef}
            sx={{
                pt: fromBlog ? 2 : 8,
                pb: 8,
                margin: "0px 6%"
            }}
        >
            {loading && !blogs && (
                <LoadingSpinner />
            )}
            <Box
                display="flex"
                gap={1.2}
                mb={1.6}
                alignItems="center"
            >
                <Box sx={{ background: '#306FE8', width: '20px', height: '20px', borderRadius: 0.6 }}></Box>
                <Typography fontSize={20}>Our DCP Blogs</Typography>
            </Box>
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
            >
                <Box>
                    <Typography fontSize={45} align="center" color={theme.palette.secondary.main} fontWeight="600" textAlign="left" sx={{ lineHeight: 1.1 }}>
                        Latest from Dream College Path
                    </Typography>
                    <Typography fontSize={20} color={theme.palette.background.dark}>News, events, and stories you don't want to miss.</Typography>
                </Box>
            </Box>
            <Grid container spacing={2}>
                {publishedBlogs.map((blog, index) => (
                    <Grid
                        key={blog.blog_id || blog.id || blog._id || index}
                        item
                        md={4}
                    >
                        <CommonCard
                            borderRadius={1}
                            padding="0px"
                            content={
                                <Box>
                                    <Box sx={{ position: 'relative' }}>
                                        {renderImage(blog)}
                                    </Box>
                                    <Box p={2} width='80%'>
                                        <Typography 
                                            fontSize={20} 
                                            sx={{ 
                                                lineHeight: 1.2,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical'
                                            }}
                                        >
                                            {blog.short_description}
                                        </Typography>
                                    </Box>
                                    <Box
                                        pl={2}
                                        pr={2}
                                        pb={1}
                                        display="flex"
                                        justifyContent="space-between"
                                        alignItems="center"
                                    >
                                        <Typography fontSize={14}>
                                            {blog.published_at ? moment(blog.published_at).format('MMM DD, YYYY') : 'Date not available'}
                                        </Typography>
                                        <Box
                                            display="flex"
                                            justifyContent="space-between"
                                            alignItems="center"
                                            gap={1}
                                            sx={{ cursor: 'pointer' }}
                                            onClick={() => navigate(`/blog/${blog.blog_id}`)}
                                        >
                                            <Typography fontSize={14} color={theme.palette.background.dark}>
                                                Read More
                                            </Typography>
                                            <East sx={{ fontSize: 18, color: theme.palette.background.dark }} />
                                        </Box>
                                    </Box>
                                </Box>
                            }
                        />
                    </Grid>
                ))}
            </Grid>
            {blogs?.pagination?.totalPages > 1 && (
                <SimplePagination
                    currentPage={pageNo}
                    totalPages={blogs?.pagination?.totalPages}
                    onPageChange={handlePageChange}
                />
            )}
        </Box>
    );
};

export default Blogs;